import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Component from '../../components/Component';
import {
  deleteUsers,
  describeUsersError,
  fetchDeleteAccess,
  lookupUsers,
} from '../../services/deleteUserApi';

const SEARCH_FIELDS = [
  { value: 'email', label: 'Email', placeholder: 'fan@example.com' },
  { value: 'user_id', label: 'User id', placeholder: '76771858' },
  { value: 'phone', label: 'Phone', placeholder: '9198...' },
];

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

function accountLabel(user) {
  if (!user) return 'this account';
  if (user.email) return user.email;
  if (user.phone) return user.phone;
  return `user ${user.id}`;
}

function roleText(role) {
  if (role === 'invitee') return 'Invitee';
  if (role === 'referrer') return 'Referrer';
  return role || '—';
}

function statusText(status) {
  if (status === true) return 'Active';
  if (status === false) return 'Inactive';
  return '—';
}

function cell(value) {
  if (value == null || value === '') return '—';
  return String(value);
}

export function DeleteUserPanel() {
  const [accessLoading, setAccessLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [accessError, setAccessError] = useState('');
  const [field, setField] = useState('email');
  const [searchValue, setSearchValue] = useState('');
  const [lookupKey, setLookupKey] = useState(null);
  const [users, setUsers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await fetchDeleteAccess();
        if (!active) return;
        setAccess(data);
      } catch (loadError) {
        if (!active) return;
        setAccess(null);
        setAccessError(describeUsersError(loadError, 'Checking delete access'));
      } finally {
        if (active) setAccessLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const enabled = access?.enabled === true;
  const selectedField = SEARCH_FIELDS.find((item) => item.value === field) || SEARCH_FIELDS[0];
  const canDelete = enabled && users.length > 0 && !lookupLoading;

  const turnOff = (reason) => {
    setAccess((current) => ({
      enabled: false,
      environment: current?.environment ?? null,
      database: current?.database ?? null,
      reason: reason || 'Delete is turned off for this environment.',
    }));
    setUsers([]);
    setReferrals([]);
    setLookupKey(null);
    setConfirmOpen(false);
    setError('');
  };

  const handleLookup = async (event) => {
    event.preventDefault();
    if (!enabled) return;
    setError('');
    setSuccess('');
    setUsers([]);
    setReferrals([]);
    setLookupKey(null);

    const value = searchValue.trim();
    if (!value) {
      setError('Enter an email, user id, or phone.');
      return;
    }
    if (field === 'user_id' && !/^\d+$/.test(value)) {
      setError('Enter a numeric user id.');
      return;
    }

    setLookupLoading(true);
    try {
      const data = await lookupUsers(field, value);
      const nextUsers = Array.isArray(data.users) ? data.users : [];
      const nextReferrals = Array.isArray(data.referrals) ? data.referrals : [];
      if (nextUsers.length === 0) {
        setError('No account matches that search.');
        return;
      }
      setUsers(nextUsers);
      setReferrals(nextReferrals);
      setLookupKey({ field, value });
    } catch (lookupError) {
      if (lookupError.status === 403) {
        turnOff(describeUsersError(lookupError, 'Lookup'));
        return;
      }
      setError(describeUsersError(lookupError, 'Lookup'));
    } finally {
      setLookupLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!enabled || !lookupKey) return;
    setDeleting(true);
    setError('');
    try {
      const data = await deleteUsers(lookupKey.field, lookupKey.value);
      const labels = (Array.isArray(data.users) ? data.users : users).map(accountLabel);
      const referralCount = data.referral_rows_deleted ?? 0;
      setSuccess(
        labels.length
          ? `Deleted ${labels.join(', ')}. Referral rows removed: ${referralCount}.`
          : `Deleted the account. Referral rows removed: ${referralCount}.`
      );
      setUsers([]);
      setReferrals([]);
      setLookupKey(null);
      setSearchValue('');
      setConfirmOpen(false);
    } catch (deleteError) {
      if (deleteError.status === 403) {
        turnOff(describeUsersError(deleteError, 'Delete'));
        return;
      }
      setError(describeUsersError(deleteError, 'Delete'));
      setConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton style={{ background: '#FFFFFF' }} component={Link} to="/" aria-label="Back">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ ml: 2 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 0 }}>
            Delete a fan account
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dev only. Removes the account so the same email or phone can sign up and be referred again.
          </Typography>
        </Box>
      </Box>

      {accessLoading && (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} aria-label="Checking delete access" />
        </Box>
      )}

      {!accessLoading && !enabled && (
        <Alert severity="warning">
          {access?.reason || accessError || 'Delete is turned off for this environment.'}
        </Alert>
      )}

      {!accessLoading && enabled && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            This runs only when the API is not production and the database is qa_Dugout
            {access.environment || access.database
              ? ` (${[access.environment, access.database].filter(Boolean).join(' · ')}).`
              : '.'}{' '}
            The account row is removed. Referral rows where this account is the invitee or the referrer are removed, so those invites leave the referrer&apos;s progress list. Fan energy already paid stays on the referrer&apos;s balance. Deleting a referrer also removes that referrer&apos;s own code.
            Sign the next test account in on a different phone and device than the referrer. The same phone or the same app install blocks the new fan energy credit.
          </Alert>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box component="form" onSubmit={handleLookup}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Search by
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={field}
                  aria-label="Search by"
                  onChange={(_, next) => {
                    if (!next) return;
                    setField(next);
                    setError('');
                  }}
                  sx={{ mb: 2 }}
                >
                  {SEARCH_FIELDS.map((item) => (
                    <ToggleButton key={item.value} value={item.value}>
                      {item.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    label="Account"
                    placeholder={selectedField.placeholder}
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    size="small"
                    sx={{ flex: '1 1 280px' }}
                    inputProps={{ 'aria-label': 'Account' }}
                  />
                  <Button type="submit" variant="contained" disabled={lookupLoading}>
                    {lookupLoading ? 'Looking up…' : 'Look up'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {users.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Accounts that will be removed ({users.length})
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Id</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Joined with</TableCell>
                      <TableCell>Referral code</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>{cell(user.id)}</TableCell>
                        <TableCell>{cell(user.email)}</TableCell>
                        <TableCell>{cell(user.username)}</TableCell>
                        <TableCell>{cell(user.name)}</TableCell>
                        <TableCell>{cell(user.phone)}</TableCell>
                        <TableCell>{statusText(user.status)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{cell(user.joined_with_code)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{cell(user.referral_code)}</TableCell>
                        <TableCell>{formatDateTime(user.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" sx={{ mb: 1 }}>
                Referral rows ({referrals.length})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Invitee means this account joined with a code. Referrer means this account invited someone else.
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Id</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Joined user</TableCell>
                      <TableCell>Referrer</TableCell>
                      <TableCell>Reward</TableCell>
                      <TableCell>Role</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {referrals.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>No referral rows for these accounts.</TableCell>
                      </TableRow>
                    )}
                    {referrals.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{cell(row.id)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{cell(row.referral_code)}</TableCell>
                        <TableCell>{cell(row.joined_user_id)}</TableCell>
                        <TableCell>{cell(row.referrer_user_id)}</TableCell>
                        <TableCell>{cell(row.reward_status)}</TableCell>
                        <TableCell>{roleText(row.role)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {canDelete && (
                <Button
                  type="button"
                  variant="contained"
                  color="error"
                  onClick={() => setConfirmOpen(true)}
                  disabled={deleting}
                >
                  {users.length > 1 ? 'Delete accounts' : 'Delete account'}
                </Button>
              )}
            </>
          )}

          <Dialog
            open={confirmOpen}
            transitionDuration={0}
            onClose={() => {
              if (!deleting) setConfirmOpen(false);
            }}
            aria-labelledby="delete-fan-title"
          >
            <DialogTitle id="delete-fan-title">Delete these accounts?</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This removes the account so the email and phone can register again. Referral rows go with it. Fan energy already paid to the referrer stays.
              </Typography>
              {users.map((user) => (
                <Typography key={user.id} variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {accountLabel(user)}
                </Typography>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Confirm delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}

function DeleteUser() {
  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header" style={{ paddingBottom: 0 }} />
          <DeleteUserPanel />
        </div>
      </div>
    </Component>
  );
}

export default DeleteUser;
